/**
 * Mission Store Utilities
 *
 * Platform-independent utility functions extracted from MissionEditorV2Store.
 * These functions provide pure, stateless operations for mission manipulation.
 *
 * @packageDocumentation
 */

import { format } from "date-fns";
import { cloneDeep } from "lodash";

import { FEET_PER_METER } from "@skydio/math";
import { RecordingMode_Enum } from "@skydio/pbtypes/pbtypes/gen/user_camera/recording_mode";
import { ReturnSettings } from "@skydio/pbtypes/pbtypes/vehicle/infrastructure/flight_deck/return_settings";
import {
  LostConnectionActionEnum_Enum,
  Mission,
  PostMissionActionEnum_Enum,
  SequenceActionArgs,
} from "@skydio/pbtypes/pbtypes/vehicle/skills/mission";

import { initActionType, ROOT_SEQUENCE_NAME } from "../actionTree";
import { getReturnSpeedConstraints } from "../constraints";

/**
 * Height frame reference for the editor
 *
 * @remarks
 * This enum describes the top level editor frame reference.
 * HAL = Height Above Launch
 * AGL = Height Above Ground (with terrain following)
 */
export enum EditorHeightFrame {
  INVALID = "INVALID", // Uninitialized state
  HAL = "HAL", // Height Above Launch
  AGL = "AGL", // Height Above Ground
}

/**
 * Height constants for terrain following safety
 */
export const MIN_HEIGHT_AGL_FEET = 100; // 100 feet minimum for terrain following
export const MIN_HEIGHT_AGL_METERS = MIN_HEIGHT_AGL_FEET / FEET_PER_METER; // ~30.48 meters

/**
 * Updates a mission's spec based on the height frame
 *
 * @remarks
 * This function handles:
 * - Updating surface scan terrain_follow setting
 * - Enforcing minimum heights for AGL mode
 * - Configuring return settings for terrain following
 *
 * @param mission - The mission to update
 * @param editorHeightFrame - The height frame to apply (HAL or AGL)
 * @returns Updated mission with height frame settings applied
 */
export function updateMissionForHeightFrame(
  mission: Mission,
  editorHeightFrame: EditorHeightFrame
): Mission {
  // If editorHeightFrame is INVALID, don't update anything
  if (editorHeightFrame === EditorHeightFrame.INVALID) {
    return mission;
  }

  const updatedMission = cloneDeep(mission);
  const rootSequence = updatedMission.actions[0]?.args?.sequence;

  // Update surface scan terrain_follow setting
  if (rootSequence?.actions && rootSequence.actions.length > 0) {
    const firstAction = rootSequence.actions[0];

    // Check if it's a surface scan action with a settings manager
    if (firstAction.args?.surfaceScanSkill?.settingsManager) {
      const settingsManager = firstAction.args.surfaceScanSkill.settingsManager;
      const shouldEnableTerrainFollow = editorHeightFrame === EditorHeightFrame.AGL;

      // Update terrain_follow if needed
      if (settingsManager.terrainFollow !== shouldEnableTerrainFollow) {
        settingsManager.terrainFollow = shouldEnableTerrainFollow;
      }

      // Enforce minimum height for AGL mode
      if (shouldEnableTerrainFollow && settingsManager.areaScanHeight < MIN_HEIGHT_AGL_METERS) {
        settingsManager.areaScanHeight = MIN_HEIGHT_AGL_METERS;
      }
    }
  }

  // Update return settings based on height frame
  if (updatedMission.rtxSettings) {
    if (editorHeightFrame === EditorHeightFrame.AGL) {
      // Enforce minimum height for AGL mode
      const returnHeight = Math.max(
        updatedMission.rtxSettings.minimumHeight,
        MIN_HEIGHT_AGL_METERS
      );

      // When AGL is set, configure for global pathfinder return with terrain following
      updatedMission.rtxSettings = {
        ...updatedMission.rtxSettings,
        useGlobalPathfinder: true,
        globalPathfinderReturnHeightAgl: returnHeight,
        minimumHeight: returnHeight,
      };
    } else {
      // When HAL is set, unset global pathfinder settings
      updatedMission.rtxSettings = {
        ...updatedMission.rtxSettings,
        useGlobalPathfinder: false,
        globalPathfinderReturnHeightAgl: 0,
      };
    }
  }

  return updatedMission;
}

/**
 * Options for validating height frame changes
 *
 * @remarks
 * These flags determine which mission settings should be checked during validation.
 */
export type HeightFrameValidationOptions = {
  /**
   * Whether to check scan settings (area scan height).
   * If false, scan height validation is skipped.
   */
  checkScanSettings: boolean;
  /**
   * Whether to check return behavior (return height and pathfinder).
   * If false, return height and pathfinder validation is skipped.
   */
  checkReturnBehavior: boolean;
};

/**
 * Validates a height frame change and returns flagged issues that require user acknowledgment
 *
 * @remarks
 * This is a pure function for testability. It checks if switching height frames
 * will cause settings to be automatically adjusted and returns warnings.
 *
 * @param mission - The current mission spec
 * @param currentHeightFrame - The current height frame
 * @param newEditorHeightFrame - The proposed new height frame
 * @param options - Validation options (which settings to check)
 * @returns Array of issue messages requiring user acknowledgment (empty if no issues)
 */
export function computeHeightFrameValidation(
  mission: Mission,
  currentHeightFrame: EditorHeightFrame,
  newEditorHeightFrame: EditorHeightFrame,
  options: HeightFrameValidationOptions
): string[] {
  // Skip validation if the height frame hasn't actually changed
  if (currentHeightFrame === newEditorHeightFrame) {
    return [];
  }

  const flaggedIssues: string[] = [];
  const { checkScanSettings, checkReturnBehavior } = options;

  // Check if switching to AGL and current settings are below minimum
  if (newEditorHeightFrame === EditorHeightFrame.AGL) {
    // Only check surface scan height if requested
    if (checkScanSettings) {
      const rootSequence = mission.actions[0]?.args?.sequence;
      if (rootSequence?.actions && rootSequence.actions.length > 0) {
        const firstAction = rootSequence.actions[0];
        if (
          firstAction.args?.surfaceScanSkill?.settingsManager &&
          firstAction.args.surfaceScanSkill.settingsManager.areaScanHeight < MIN_HEIGHT_AGL_METERS
        ) {
          flaggedIssues.push(`Scan height will be increased to ${MIN_HEIGHT_AGL_FEET} ft.`);
        }
      }
    }

    // Only check return behavior settings if requested
    if (checkReturnBehavior) {
      if (mission.rtxSettings && mission.rtxSettings.minimumHeight < MIN_HEIGHT_AGL_METERS) {
        flaggedIssues.push(`Return height will be increased to ${MIN_HEIGHT_AGL_FEET} ft.`);
      }

      const returnHeightFt = Math.round(
        Math.max(mission.rtxSettings?.minimumHeight || 0, MIN_HEIGHT_AGL_METERS) * FEET_PER_METER
      );
      flaggedIssues.push(`Vehicle will use Pathfinder for return at ${returnHeightFt} ft AGL.`);
    }
  } else {
    // Switching from AGL to HAL - only show return info if requested
    if (checkReturnBehavior) {
      const rtxSettings = mission.rtxSettings;
      const returnType = rtxSettings?.useBacktrack ? "Backtrack" : "Up and Over";
      const returnBehavior = rtxSettings?.ascendFromCurrentHeight
        ? "Above Vehicle"
        : "Above Launch";
      const returnHeightFt = Math.round((rtxSettings?.minimumHeight || 0) * FEET_PER_METER);
      flaggedIssues.push(
        `Vehicle will use ${returnType} return at ${returnHeightFt} ft ${returnBehavior}`
      );
    }
  }

  return flaggedIssues;
}

/**
 * Creates a unique mission name with timestamp
 *
 * @param displayName - The base display name for the mission
 * @returns Unique mission name with timestamp
 *
 * @example
 * ```typescript
 * const name = createUniqueMissionName("My Mission");
 * // Returns: "My Mission 01/15/2024 14:30:45"
 * ```
 */
export function createUniqueMissionName(displayName: string): string {
  return `${displayName} ${format(new Date(), "MM/dd/yyyy HH:mm:ss")}`;
}

/**
 * Zone edge type for parsing geofence data
 *
 * @remarks
 * This type represents the structure of zone edges from GraphQL/database queries.
 * It's intentionally flexible to work with different API shapes.
 */
export type ZoneEdge = {
  node?: {
    uuid: string;
    name: string | null;
    keepOut: boolean;
    polygon: {
      type: "Polygon";
      coordinates: number[][][];
    };
    ceiling: number;
    ceilingEnabled: boolean;
    floor: number;
    floorEnabled: boolean;
  } | null;
} | null;

/**
 * Parsed zone data structure
 */
export type Zone = {
  uuid: string;
  name: string | null;
  keepOut: boolean;
  polygon: {
    type: "Polygon";
    coordinates: number[][][];
  };
  ceiling: number;
  ceilingEnabled: boolean;
  floor: number;
  floorEnabled: boolean;
};

/**
 * Parses zone edges from GraphQL response into a usable geofence structure
 *
 * @remarks
 * This function:
 * - Filters out null/undefined edges
 * - Removes the last coordinate from each polygon (which is a duplicate of the first)
 * - Returns a clean array of zone objects
 *
 * @param zoneEdges - Array of zone edges from GraphQL query
 * @returns Array of parsed zone objects
 */
export function parseZoneEdgesIntoGeofence(zoneEdges: readonly ZoneEdge[]): Zone[] {
  return zoneEdges.reduce<Zone[]>((res, edge) => {
    const zoneNode = edge?.node;
    if (!zoneNode) return res;

    // Create a polygon
    const polygon = zoneNode.polygon;
    // Then drop the last point since it's the same as the first
    // and is used to respect the fact that the Geometry type
    // expects a closed polygon.
    // We don't need the polygon to be closed in the frontend app.
    // We then re-add it before saving to the DB.
    const coordinates = polygon.coordinates[0].slice(0, -1);
    // Remove the wraparound coordinate but make it back into a Polygon
    res.push({
      ...zoneNode,
      polygon: {
        ...polygon,
        coordinates: [coordinates],
      },
    });
    return res;
  }, []);
}

/**
 * Default return settings for missions
 */
export const DEFAULT_RTX_SETTINGS: ReturnSettings = ReturnSettings.fromPartial({
  useBacktrack: false,
  ascendFromCurrentHeight: false,
  minimumHeight: 30.48, // 100ft
  waitTime: 60,
  speed: getReturnSpeedConstraints("nominal", { vehicleType: "UNKNOWN" }).DEFAULT,
  faceForward: true,
});

/**
 * Default post-mission actions for dock-based missions
 */
export const DEFAULT_POST_MISSION_ACTIONS = {
  postMissionAction: PostMissionActionEnum_Enum.LAND_AT_DOCK,
  postFailureAction: PostMissionActionEnum_Enum.LAND_AT_DOCK,
};

/**
 * Legacy post-mission actions for non-dock missions
 */
export const LEGACY_DOCKLESS_POST_MISSION_ACTIONS = {
  postMissionAction: PostMissionActionEnum_Enum.RETURN_TO_HOME,
  postFailureAction: PostMissionActionEnum_Enum.RETURN_TO_HOME,
};

/**
 * Creates a default mission specification
 *
 * @remarks
 * This creates a minimal valid mission with:
 * - Default recording mode
 * - Default return settings
 * - Empty root sequence
 * - Default post-mission actions
 *
 * @returns A new default mission spec
 */
export function createDefaultMissionSpec(): Mission {
  return Mission.fromPartial({
    recordingMode: RecordingMode_Enum.VIDEO_1080P_30FPS,
    rtxSettings: DEFAULT_RTX_SETTINGS,
    useRtxSettings: true,
    autoStart: true,
    dockMission: true,
    actions: [
      initActionType(
        "sequence",
        SequenceActionArgs.fromPartial({
          name: ROOT_SEQUENCE_NAME,
          actions: [],
        })
      ),
    ],
    ...DEFAULT_POST_MISSION_ACTIONS,
    lostConnectionAction: LostConnectionActionEnum_Enum.RETURN_TO_HOME,
    autonomousAbortMissionOnFailedAction: true,
    disableStrobeLights: false,
    showSkipUi: true,
    useRecordingMode: true,
  });
}
